import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IPromsRange } from "../../../../../@types/Proms";
import { ISurveyDisplayRules } from "../../../../../@types/Surveys";
import { GraphTypes } from "../../../../../constants/surveys";
import { DATE_FILTER_CUSTOM } from "../../../../PatientPage/components/MeasurementModal/components/CustomOptionsBar/dateFilterOptions";
import { PromsDomainTotalGraph } from "../../../../PatientPage/components/PatientPromsTable/components/PromsDomainTotalGraph";
import { PromsLineGraph } from "../../../../PatientPage/components/PatientPromsTable/components/PromsLineGraph";
import { PromsScatterGraph } from "../../../../PatientPage/components/PatientPromsTable/components/PromsScatterGraph";
import { MutedText } from "../../SurveyBuilder.styles";
import { GraphCanvas } from "./PreviewTab.styles";

const SAMPLE_POINTS = 8;
const noop = () => {};

/**
 * Sample scores, spread over the axis so every range shows: a wave between min and max, one
 * answer a week. Deterministic, so the preview doesn't jump between renders.
 */
export function sampleScores(min: number, max: number, phase = 0) {
  const span = max - min;
  return Array.from({ length: SAMPLE_POINTS }, (_, i) => {
    const wave = (Math.sin((i + phase) * 1.3) + 1) / 2;
    return Math.round((min + wave * span) * 10) / 10;
  });
}

const sampleTime = (i: number) =>
  dayjs()
    .subtract((SAMPLE_POINTS - 1 - i) * 7, "day")
    .startOf("day");

function mainValues(scores: number[]) {
  return {
    min: Math.min(...scores),
    max: Math.max(...scores),
    average:
      Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) /
      100,
    minDate: sampleTime(0),
    maxDate: sampleTime(SAMPLE_POINTS - 1),
  };
}

/** EN-5261: the version's graph on made-up answers, to check ranges, colours and legends. */
export function PortalGraphPreview({
  displayRules,
}: {
  displayRules?: ISurveyDisplayRules;
}) {
  const { t } = useTranslation();

  const dateInterval = useMemo(
    () => ({
      id: DATE_FILTER_CUSTOM,
      startDate: sampleTime(0),
      endDate: dayjs().endOf("day"),
    }),
    []
  );

  if (!displayRules || displayRules.type === GraphTypes.tableView) {
    return <MutedText>{t("surveys_builder_preview_noGraph")}</MutedText>;
  }

  const min = displayRules.yAxisMin ?? 0;
  const max = displayRules.yAxisMax ?? 100;

  if (displayRules.type === GraphTypes.totalScoreGraphWithLegend) {
    const scores = sampleScores(min, max);
    const data = scores.map((totalScore, i) => ({
      id: i,
      time: sampleTime(i).toISOString(),
      totalScore,
    }));
    const ranges: IPromsRange[] = (displayRules.ranges ?? []).map((r) => ({
      start: r.start,
      end: r.end,
      startComparator: r.startComparator ?? ">=",
      endComparator: r.startComparator !== "=" ? r.endComparator : "<",
      color: r.color,
      name: r.name,
    }));

    return (
      <GraphCanvas>
        <PromsLineGraph
          data={data}
          minY={min}
          maxY={max}
          mainValues={mainValues(scores)}
          dateInterval={dateInterval}
          showValues
          ranges={ranges}
          onDotClick={noop}
        />
      </GraphCanvas>
    );
  }

  if (displayRules.type === GraphTypes.domainAndTotalGraphWithLegend) {
    const legends = displayRules.domainAndTotalLegends ?? [];
    const data = legends.flatMap((legend, l) =>
      sampleScores(min, max, l * 2).map((score, i) => ({
        id: i,
        time: sampleTime(i).toISOString(),
        score,
        domain: legend.domain,
      }))
    );

    return (
      <GraphCanvas>
        <PromsDomainTotalGraph
          data={data}
          minY={min}
          maxY={max}
          mainValues={mainValues(data.map((d) => d.score))}
          dateInterval={dateInterval}
          showValues
          legends={legends.map((legend) => ({ ...legend, id: legend.domain }))}
          onDotClick={noop}
        />
      </GraphCanvas>
    );
  }

  if (displayRules.type === GraphTypes.answerPlot) {
    const labels = [...(displayRules.yAxisLabels ?? [])].sort(
      (a, b) => a.order - b.order
    );
    const legends = [...(displayRules.legends ?? [])].sort(
      (a, b) => a.order - b.order
    );
    const data =
      legends.length === 0
        ? []
        : Array.from({ length: SAMPLE_POINTS }).flatMap((_, i) =>
            labels.map((label, l) => ({
              y: label.text,
              x: sampleTime(i).valueOf(),
              value: legends[(i + l) % legends.length].text,
              surveyId: i,
            }))
          );

    return (
      <GraphCanvas>
        <PromsScatterGraph
          data={data}
          minDate={sampleTime(0)}
          maxDate={sampleTime(SAMPLE_POINTS - 1)}
          dateInterval={dateInterval}
          yAxisLabels={labels.map((label) => label.text)}
          legendsColorMap={Object.fromEntries(
            legends.map((legend) => [legend.text, legend.color])
          )}
          onDotClick={noop}
        />
      </GraphCanvas>
    );
  }

  return null;
}
